import React, { createContext, useReducer, useContext, FC } from 'react';

enum UserTypes {
  Login = 'LOGIN_SUCCESS',
  SignOut = 'SIGN_OUT_SUCCESS',
}

enum UserInfoTypes {
  success = 'INFO_SUCCESS',
  error = 'INFO_FAILED',
  signOut = 'SIGN_OUT_SUCCESS',
}

const UserStateContext = createContext<Partial<IUserStateContext>>({});
const UserDispatchContext = createContext<Partial<IUserDispatchContext>>({});

const userReducer = (state: TUserState, action: TUserAction): TUserState => {
  switch (action.type) {
    case UserTypes.Login:
      return {
        ...state,
        isAuthenticated: true,
        email: action.email,
        token: action.token,
      };
    case UserTypes.SignOut:
      return {
        ...state,
        isAuthenticated: false,
        email: '',
        token: '',
      };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const userInfoReducer = (state: TUserInfoState, action: TUserInfoAction): TUserInfoState => {
  switch (action.type) {
    case UserInfoTypes.success:
      return {
        role: action.role,
      };
    case UserInfoTypes.error:
      return {
        role: '',
      };
    case UserInfoTypes.signOut:
      return { role: '' };
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const UserProvider: FC = ({ children }) => {
  const idToken = localStorage.getItem('id_token');
  const idEmail = localStorage.getItem('id_email');
  const [state, loginDispatch] = useReducer(userReducer, {
    isAuthenticated: !!idToken,
    email: idEmail || '',
    token: idToken || '',
  });

  const [info, infoDispatch] = useReducer(userInfoReducer, {
    role: '',
  });

  return (
    <UserStateContext.Provider
      value={{
        user: state,
        userInfo: info,
      }}>
      <UserDispatchContext.Provider
        value={{
          user: loginDispatch,
          userInfo: infoDispatch,
        }}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
};

const useUserState = (): Partial<TUserState> => {
  const context = useContext(UserStateContext).user;
  if (context === undefined) {
    throw new Error('useUserState must be used within a UserProvider');
  }
  return context;
};

const useInfoUserState = (): Partial<TUserInfoState> => {
  const context = useContext(UserStateContext).userInfo;
  if (context === undefined) {
    throw new Error('useUserState must be used within a UserProvider');
  }
  return context;
};

const useUserDispatch = () => {
  const context = useContext(UserDispatchContext).user;
  if (context === undefined) {
    throw new Error('useUserDispatch must be used within a UserProvider');
  }
  return context;
};

const useInfoUserDispatch = () => {
  const context = useContext(UserDispatchContext).userInfo;
  if (context === undefined) {
    throw new Error('useUserDispatch must be used within a UserProvider');
  }
  return context;
};

export { UserProvider, useUserState, useInfoUserState, useUserDispatch, useInfoUserDispatch };
